import { Node, mergeAttributes } from '@tiptap/core';
import { Node as ProseMirrorNode } from 'prosemirror-model';

export interface CodeBlockOptions {
  HTMLAttributes: Record<string, unknown>;
  languageClassPrefix: string;
}

export const CodeBlockWithCopy = Node.create<CodeBlockOptions>({
  name: 'codeBlock',

  group: 'block',

  content: 'text*',

  marks: '',

  code: true,

  defining: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      languageClassPrefix: 'language-',
    };
  },

  addAttributes() {
    return {
      language: {
        default: null,
        parseHTML: element => {
          const { languageClassPrefix } = this.options;
          const classNames = [...(element.firstElementChild?.classList || [])];
          const languages = classNames
            .filter(className => className.startsWith(languageClassPrefix))
            .map(className => className.replace(languageClassPrefix, ''));
          const language = languages[0];

          if (!language) {
            return null;
          }

          return language;
        },
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'pre',
        preserveWhitespace: 'full',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'pre',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      [
        'code',
        {
          class: node.attrs['language']
            ? this.options.languageClassPrefix + node.attrs['language']
            : null,
        },
        0,
      ],
    ];
  },

  addCommands() {
    return {
      setCodeBlock:
        attributes =>
        ({ commands }) => {
          return commands.setNode(this.name, attributes);
        },
      toggleCodeBlock:
        attributes =>
        ({ commands }) => {
          return commands.toggleNode(this.name, 'paragraph', attributes);
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Alt-c': () => this.editor.commands.toggleCodeBlock(),
      Backspace: () => {
        const { empty, $anchor } = this.editor.state.selection;
        const isAtStart = $anchor.pos === 1;

        if (!empty || $anchor.parent.type.name !== this.name) {
          return false;
        }

        if (isAtStart || !$anchor.parent.textContent.length) {
          return this.editor.commands.clearNodes();
        }

        return false;
      },
    };
  },

  addNodeView() {
    return ({ node, editor }) => {
      let currentNode = node;

      const wrapper = document.createElement('div');
      wrapper.className = 'code-block-wrapper';
      // display:table (rather than the default block, which stretches to fill the editor's full
      // width) sizes the box to its widest content line instead — while still laying out as its
      // own block on its own line. max-width caps it at the editor's width for long lines, which
      // still scroll horizontally via the pre's own overflow-x below; min-width keeps the box wide
      // enough that the copy button (positioned absolute, top-right) never overflows past a very
      // short one-line snippet.
      wrapper.style.cssText = 'position: relative; margin: 0.5rem 0; display: table; max-width: 100%; min-width: 12rem;';

      const pre = document.createElement('pre');
      pre.style.cssText = 'margin: 0; padding: 1rem; padding-top: 2.5rem; background-color: #1e293b; color: #e2e8f0; border-radius: 0.375rem; overflow-x: auto; font-family: monospace; font-size: 0.875rem; line-height: 1.5;';

      const code = document.createElement('code');
      if (node.attrs['language']) {
        code.className = this.options.languageClassPrefix + node.attrs['language'];
      }

      pre.appendChild(code);

      // Create copy button
      const copyButton = document.createElement('button');
      copyButton.className = 'code-block-copy-btn';
      copyButton.type = 'button';
      copyButton.title = 'Copy code';
      copyButton.innerHTML = `
        <span class="material-icons copy-icon" style="font-size: 18px;">content_copy</span>
        <span class="copied-text" style="font-size: 12px; display: none; white-space: nowrap; margin-left: 0.25rem;">Copied!</span>
      `;
      copyButton.style.cssText = `
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        padding: 0.375rem 0.5rem;
        background-color: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 0.25rem;
        color: #e2e8f0;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
        z-index: 10;
        font-family: inherit;
      `;

      // Hover effect
      copyButton.addEventListener('mouseenter', () => {
        copyButton.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
        copyButton.style.borderColor = 'rgba(255, 255, 255, 0.3)';
      });

      copyButton.addEventListener('mouseleave', () => {
        copyButton.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
        copyButton.style.borderColor = 'rgba(255, 255, 255, 0.2)';
      });

      // Copy functionality
      copyButton.addEventListener('mousedown', (e) => {
        // Keep editor selection stable when interacting with the utility button.
        e.preventDefault();
      });

      copyButton.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const copyIcon = copyButton.querySelector('.copy-icon') as HTMLElement;
        const copiedText = copyButton.querySelector('.copied-text') as HTMLElement;

        try {
          await navigator.clipboard.writeText(currentNode.textContent);

          // Show success feedback
          copyIcon.style.display = 'none';
          copiedText.style.display = 'inline';
          copyButton.style.backgroundColor = 'rgba(34, 197, 94, 0.2)';
          copyButton.style.borderColor = 'rgba(34, 197, 94, 0.4)';
          copyButton.style.color = '#86efac';
          copyButton.title = 'Copied!';

          // Reset after 2 seconds
          setTimeout(() => {
            copyIcon.style.display = 'inline';
            copiedText.style.display = 'none';
            copyButton.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            copyButton.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            copyButton.style.color = '#e2e8f0';
            copyButton.title = 'Copy code';
          }, 2000);
        } catch (err) {
          console.error('Failed to copy code:', err);
        }
      });

      wrapper.appendChild(pre);
      wrapper.appendChild(copyButton);

      return {
        dom: wrapper,
        contentDOM: code,
        update: (updatedNode: ProseMirrorNode) => {
          if (updatedNode.type.name !== this.name) {
            return false;
          }

          currentNode = updatedNode;

          // Update language class if changed
          code.className = updatedNode.attrs['language']
            ? this.options.languageClassPrefix + updatedNode.attrs['language']
            : '';

          return true;
        },
        ignoreMutation: (mutation) => {
          // Ignore DOM updates from the copy button itself.
          return mutation.target instanceof globalThis.Node
            ? copyButton.contains(mutation.target)
            : false;
        },
      };
    };
  },
});
